import moment from "moment";

// Day report line times are always Amsterdam (NL). Grouping and labelling must
// use the server's Amsterdam-formatted `date` (YYYY-MM-DD) and `time` (HH:mm)
// strings, never a browser-local reinterpretation of `date_time` — otherwise a
// device in another timezone shifts lines into the wrong hour, and across
// midnight into the wrong day.

/**
 * Chronological "YYYY-MM-DD HH" group key for a line, in Amsterdam time.
 * @param {{date?: string, time?: string}} line
 * @returns {string}
 */
export function hourGroupKey(line) {
  const date = line.date || "";
  const time = line.time || "";
  return `${date} ${time.slice(0, 2)}`;
}

/**
 * Chronological "YYYY-MM-DD HH:mm" sort key for a line, in Amsterdam time.
 * @param {{date?: string, time?: string}} line
 * @returns {string}
 */
export function sortKey(line) {
  return `${line.date || ""} ${line.time || ""}`;
}

/**
 * Group day report lines into per-hour buckets, keyed and sorted chronologically
 * by Amsterdam time. Lines within a bucket are sorted earliest-first.
 *
 * @param {Array<object>} lines
 * @param {{ importantOnly?: boolean }} [options]
 * @returns {Record<string, Array<object>>} ordered object of hour key -> lines
 */
export function groupDayReportLinesByHour(
  lines,
  { importantOnly = false } = {},
) {
  const source = Array.isArray(lines) ? lines : [];
  const filtered = importantOnly
    ? source.filter((line) => line.important === true)
    : source;

  const sorted = [...filtered].sort((a, b) => {
    const ka = sortKey(a);
    const kb = sortKey(b);
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });

  const grouped = {};
  for (const line of sorted) {
    const key = hourGroupKey(line);
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(line);
  }

  // Re-insert keys in chronological order (string sort works on the key format).
  const ordered = {};
  for (const key of Object.keys(grouped).sort()) {
    ordered[key] = grouped[key];
  }
  return ordered;
}

/**
 * Human label for an hour block, e.g. "06 Aug @ 13:00", in Amsterdam time.
 * Built from `date`/`time`; `moment(date, "YYYY-MM-DD")` parses a date-only
 * value so there is no timezone shift.
 * @param {{date?: string, time?: string}} line
 * @returns {string}
 */
export function formatHourLabel(line) {
  const day = moment(line.date, "YYYY-MM-DD").format("DD MMM");
  const hour = (line.time || "00:00").slice(0, 2);
  return `${day} @ ${hour}:00`;
}
